/**
 * IsaiahAnimationLibrary v4
 * Extended with world-specific roles, dialogue, and context.
 *
 * ============================================================
 * CANONICAL ISAIAH CHARACTER SPECIFICATION
 * ============================================================
 * This is the LOCKED-IN, approved mascot. ALL pages must use
 * this exact character. No variations in appearance are allowed.
 *
 * IMAGE: /assets/isaiah/poses/idle.png
 *
 * APPEARANCE (DO NOT DEVIATE):
 *  - Hair: Low cut fade, very short, no height/volume/afro
 *  - Face: Forward-facing, warm medium-brown skin, large friendly eyes
 *  - Expression: Open-mouth smile, cheerful
 *  - Outfit: Royal blue hoodie, yellow star on chest, drawstrings visible
 *  - Hands: Both tucked in hoodie front pocket
 *  - Pants: Dark gray/charcoal joggers
 *  - Shoes: High-top sneakers, white/black/green accents
 *  - Build: Average child proportions, not stocky, not thin
 *
 * RULES:
 *  1. NEVER generate or use alternate pose images, they produce
 *     inconsistent characters (different face, hair, proportions).
 *  2. The ONLY image used is idle.png. All pose keys resolve to it.
 *  3. Outfit changes per world page are handled by SEPARATE dedicated
 *     images that must be hand-approved before use.
 *  4. Hair must NEVER be higher than the approved idle.png reference.
 *  5. Skin tone, height, frame, facial features must remain identical.
 * ============================================================
 */

// Single canonical image, used for ALL pages
const CANONICAL_IMAGE = "/assets/isaiah/poses/idle.png";

// Only two approved images exist: idle and reading (both from the same generation)
const APPROVED_POSES = {
  idle: CANONICAL_IMAGE,
  reading: "/assets/isaiah/poses/reading.png",
};

export const ISAIAH_POSES = {
  idle: CANONICAL_IMAGE,
  neutral: CANONICAL_IMAGE,
  wave: CANONICAL_IMAGE,
  point: CANONICAL_IMAGE,
  think: CANONICAL_IMAGE,
  celebrate: CANONICAL_IMAGE,
  reading: APPROVED_POSES.reading,
  encouraging: CANONICAL_IMAGE,
  cheer: CANONICAL_IMAGE,
  run: CANONICAL_IMAGE,
  build: CANONICAL_IMAGE,
};

// No world-specific outfits, all pages use canonical idle
export const getWorldOutfitImage = () => CANONICAL_IMAGE;

// Animation class mappings per pose
export const POSE_ANIMATIONS = {
  idle: "isaiah-animate-idle",
  neutral: "isaiah-animate-neutral",
  wave: "isaiah-animate-wave",
  point: "isaiah-animate-point",
  think: "isaiah-animate-think",
  celebrate: "isaiah-animate-celebrate",
  reading: "isaiah-animate-reading",
  encouraging: "isaiah-animate-encouraging",
  cheer: "isaiah-animate-cheer",
  run: "isaiah-animate-run",
  build: "isaiah-animate-build",
  // Aliases for learning states
  curious: "isaiah-animate-think",
  focused: "isaiah-animate-point",
  proud: "isaiah-animate-celebrate",
};

// Learning state to pose mapping
export const LEARNING_STATE_POSES = {
  reading: "reading",
  thinking: "think",
  curious: "think",
  focused: "point",
  encouraging: "encouraging",
  proud: "encouraging",
  celebrate: "celebrate",
};

// Size variants (pixel dimensions)
export const SIZE_VARIANTS = {
  icon: { width: 40, height: 56 },
  bust: { width: 80, height: 80 },
  "half-body": { width: 120, height: 140 },
  "full-body": { width: 180, height: 260 },
  hero: { width: 260, height: 380 },
};

// Contextual pose mapping for world portals (Phase 1)
export const WORLD_CONTEXT_POSES = {
  learn: { pose: "reading", message: "Time to learn something amazing!" },
  arcade: { pose: "celebrate", message: "Let's play some games!" },
  adventure: { pose: "point", message: "Adventure awaits over there!" },
  art: { pose: "wave", message: "Let's create something beautiful!" },
  default: { pose: "idle", message: "Welcome to Isaiah's World!" },
};

// Learning Forest area pose mapping
export const LEARNING_AREA_POSES = {
  spelling: { pose: "reading", message: "Spelling strengthens your word power!" },
  math: { pose: "point", message: "Numbers are like puzzles waiting to be solved!" },
  reading: { pose: "reading", message: "Every book is a new adventure!" },
  science: { pose: "encouraging", message: "Let's discover how the world works!" },
  "social-studies": { pose: "point", message: "History has so many stories to tell!" },
  languages: { pose: "wave", message: "Bonjour! Let's learn together!" },
  tutor: { pose: "encouraging", message: "Ask me anything, I'm here to help!" },
  upload: { pose: "encouraging", message: "Let's turn your homework into something fun!" },
  default: { pose: "idle", message: "Pick a path and let's explore!" },
};

// ===== WORLD ROLES (Phase 3) =====

export const WORLD_ROLES = {
  arcade: {
    title: "Hype Captain",
    defaultPose: "celebrate",
    idlePose: "celebrate",
    color: "#A855F7",
    accentColor: "#06B6D4",
  },
  adventure: {
    title: "Explorer Leader",
    defaultPose: "point",
    idlePose: "point",
    color: "#F59E0B",
    accentColor: "#D97706",
  },
  creator: {
    title: "Build Coach",
    defaultPose: "encouraging",
    idlePose: "encouraging",
    color: "#EC4899",
    accentColor: "#8B5CF6",
  },
};

// ===== DIALOGUE SYSTEM v2, World-aware =====

export const LEARNING_DIALOGUE = {
  welcome: [
    "Welcome back, explorer!",
    "Ready to learn something new?",
    "The Learning Forest awaits!",
    "Great to see you here!",
    "Let's grow our knowledge today!",
  ],
  guidance: [
    "Want to start here?",
    "Let's try this next.",
    "This path is great for growing your skills.",
    "I think you'll enjoy this one!",
    "How about we explore this together?",
  ],
  correct: [
    "Nice work!",
    "You got it!",
    "That's right!",
    "Awesome!",
    "Nailed it!",
    "Great thinking!",
    "You're on fire!",
    "Brilliant!",
  ],
  wrong: [
    "Keep going, explorer.",
    "Almost! Try again.",
    "You're getting closer!",
    "Don't give up!",
    "Every mistake helps you learn.",
    "Let's try a different approach.",
  ],
  encouragement: [
    "You're getting stronger!",
    "Keep it up!",
    "You're doing great!",
    "I believe in you!",
    "One step at a time!",
    "You've got this!",
  ],
  completion: [
    "You did it!",
    "Great job, explorer!",
    "You earned those stars!",
    "Another challenge conquered!",
    "Look how far you've come!",
    "That was amazing!",
  ],
  stars: [
    "Stars earned!",
    "Your collection is growing!",
    "Those stars are well-deserved!",
    "Shining bright!",
  ],
  hint: [
    "Try sounding it out!",
    "Think step by step.",
    "Take your time, no rush.",
    "Read it carefully one more time.",
    "Break it into smaller pieces.",
  ],
  retry: [
    "Let's give it another shot!",
    "Every explorer tries again.",
    "You'll get it this time!",
    "Practice makes perfect!",
  ],
};

// World-specific dialogue banks (Phase 3)
export const WORLD_DIALOGUE = {
  arcade: {
    welcome: [
      "Game time! Let's go!",
      "The arcade is LIT today!",
      "Player One, ready?",
      "Power up! Games are waiting!",
      "Who's ready to set some records?!",
    ],
    hype: [
      "You're on FIRE!",
      "INSANE play!",
      "Nobody can stop you!",
      "That's what I'm talking about!",
      "LEGENDARY move!",
      "The crowd goes wild!",
    ],
    gameStart: [
      "Let's get this W!",
      "Show them what you've got!",
      "Time to shine!",
      "Bring the energy!",
      "Game face ON!",
    ],
    win: [
      "CHAMPION! That was epic!",
      "Victory! You crushed it!",
      "W! W! W!",
      "Another win for the record books!",
      "Undefeated! Unstoppable!",
    ],
    loss: [
      "Run it back! You've got this!",
      "That was close, next game is yours!",
      "Every pro loses before they win!",
      "Shake it off and try again!",
      "The comeback starts NOW!",
    ],
    browse: [
      "So many games to play!",
      "Check out the new ones!",
      "I dare you to try that game!",
      "Which one should we dominate?",
    ],
  },
  adventure: {
    welcome: [
      "Adventurer! New lands await!",
      "What mysteries will we uncover?",
      "The map is calling us!",
      "Explorer mode: ACTIVATED!",
      "Ready for the unknown?",
    ],
    explore: [
      "I see something over there!",
      "What could be hiding here?",
      "The trail leads this way!",
      "My explorer sense is tingling!",
      "There's treasure nearby!",
      "Follow my lead!",
    ],
    discovery: [
      "Look what we found!",
      "Amazing discovery!",
      "This is incredible!",
      "We're making history!",
      "The map didn't show THIS!",
    ],
    storyStart: [
      "Once upon a time...",
      "A new chapter begins!",
      "Let the adventure unfold!",
      "Every story needs a brave hero!",
    ],
    riddle: [
      "Hmm, this is a tricky one...",
      "Put your thinking cap on!",
      "I bet you can solve it!",
      "Think outside the box!",
    ],
    complete: [
      "What an adventure!",
      "We made it!",
      "The treasure is ours!",
      "Legendary explorers!",
    ],
  },
  creator: {
    welcome: [
      "Ready to build something epic?",
      "Your imagination is the blueprint!",
      "Creator mode: ENGAGED!",
      "Let's make something amazing!",
      "The canvas is all yours!",
    ],
    encourage: [
      "That looks incredible!",
      "Great creative choice!",
      "I love where this is going!",
      "You have serious talent!",
      "Keep adding, it's getting awesome!",
      "This is going to be a masterpiece!",
    ],
    tip: [
      "Try mixing different themes!",
      "Add some characters for life!",
      "Effects make worlds pop!",
      "Every world needs a story!",
      "Don't forget to save your work!",
    ],
    save: [
      "Saved! The world can see it now!",
      "Published! Time to share!",
      "Your creation is live!",
      "Another masterpiece in the gallery!",
    ],
    browse: [
      "So many cool worlds!",
      "These creators are talented!",
      "Get inspired by others!",
      "Wow, check that one out!",
    ],
  },
};

// Get random dialogue for a context
export const getDialogue = (context) => {
  const messages = LEARNING_DIALOGUE[context] || LEARNING_DIALOGUE.encouragement;
  return messages[Math.floor(Math.random() * messages.length)];
};

// Get world-specific dialogue
export const getWorldDialogue = (world, context) => {
  const worldBank = WORLD_DIALOGUE[world];
  if (!worldBank) return getDialogue(context);
  const messages = worldBank[context] || worldBank.welcome;
  return messages[Math.floor(Math.random() * messages.length)];
};

// Floating companion messages by page context
export const COMPANION_MESSAGES = {
  home: [
    "Welcome back! Where should we explore today?",
    "The worlds are waiting for you!",
    "I see some new adventures on the map!",
  ],
  learn: [
    "Ready to learn something new?",
    "Knowledge is the greatest superpower!",
    "Let's tackle today's challenges!",
    "The Learning Forest is full of secrets!",
    "Pick a subject and let's dive in!",
  ],
  "learn-activity": [
    "You're doing great!",
    "Take your time, I'm right here.",
    "Focus is your superpower!",
    "Every answer makes you smarter!",
  ],
  arcade: [
    "Game time! Let's set a new high score!",
    "Which game should we dominate first?",
    "I bet you can beat that record!",
    "Power up! Games are calling!",
    "The arcade never sleeps!",
  ],
  adventure: [
    "There's a story waiting to be told!",
    "Every great explorer needs a great adventure!",
    "Let's discover something incredible!",
    "The map is glowing, mysteries await!",
    "Follow the trail, adventurer!",
  ],
  creator: [
    "Your imagination is the only limit!",
    "Let's make something amazing!",
    "Every masterpiece starts with one idea!",
    "Build it, share it, love it!",
    "The tools are ready, are you?",
  ],
  art: [
    "Your imagination is the only limit!",
    "Let's make something amazing!",
    "Every masterpiece starts with one idea!",
  ],
  default: [
    "Need any help? I'm right here!",
    "Tap me if you need anything!",
    "Exploring is always better together!",
  ],
};

export const getContextMessage = (context) => {
  const messages = COMPANION_MESSAGES[context] || COMPANION_MESSAGES.default;
  return messages[Math.floor(Math.random() * messages.length)];
};

export const getWorldPose = (worldId) => {
  return WORLD_CONTEXT_POSES[worldId] || WORLD_CONTEXT_POSES.default;
};

export const getLearningAreaPose = (areaId) => {
  return LEARNING_AREA_POSES[areaId] || LEARNING_AREA_POSES.default;
};

export const getWorldRole = (worldId) => {
  return WORLD_ROLES[worldId] || null;
};
